import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPayouts = () => {
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [statusFilter, setStatusFilter] = useState("");

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
      });
      
      if (statusFilter) params.append("status", statusFilter);
      
      const response = await axios.get(`${API}/admin/payouts?${params}`, { withCredentials: true });
      setPayouts(response.data.payouts);
      setPagination(response.data.pagination);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error("Failed to load payouts");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [pagination.page, statusFilter]);

  const handleMarkPaid = async (commissionId) => {
    try {
      await axios.post(`${API}/admin/payouts/${commissionId}/mark-paid`, {}, { withCredentials: true });
      toast.success("Payout marked as paid");
      fetchPayouts();
    } catch (error) {
      toast.error("Failed to update payout");
      console.error(error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Payouts</h1>
          <p className="text-slate-400 mt-1">Manage partner commission payouts</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Pending Payouts</p>
                <p className="text-2xl font-bold text-amber-400">
                  ${summary.pending_amount?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-500">{summary.pending_count || 0} payouts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Paid Payouts</p>
                <p className="text-2xl font-bold text-emerald-400">
                  ${summary.paid_amount?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-500">{summary.paid_count || 0} payouts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPagination(prev => ({ ...prev, page: 1 })); }}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-slate-300">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            Payout History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 bg-slate-700" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Partner</TableHead>
                    <TableHead className="text-slate-400">Month</TableHead>
                    <TableHead className="text-slate-400 text-right">Amount</TableHead>
                    <TableHead className="text-slate-400">Payout Method</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Payment Date</TableHead>
                    <TableHead className="text-slate-400 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                        No payouts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payouts.map((payout, idx) => (
                      <TableRow key={idx} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell>
                          <p className="text-white font-medium">{payout.partner_name}</p>
                          <p className="text-sm text-slate-400">{payout.partner_email}</p>
                        </TableCell>
                        <TableCell className="text-slate-300">{payout.commission_month}</TableCell>
                        <TableCell className="text-right text-emerald-400 font-bold">
                          ${payout.amount?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell className="text-slate-300 capitalize">
                          {payout.payout_method || "PayPal"}
                        </TableCell>
                        <TableCell>
                          <Badge className={payout.payment_status === "paid" 
                            ? "bg-emerald-900/50 text-emerald-300 border-emerald-700" 
                            : "bg-amber-900/50 text-amber-300 border-amber-700"
                          }>
                            {payout.payment_status === "paid" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                            {payout.payment_status === "pending" && <Clock className="w-3 h-3 mr-1" />}
                            {payout.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {payout.payment_date ? new Date(payout.payment_date).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {payout.payment_status === "pending" && (
                            <Button 
                              size="sm" 
                              onClick={() => handleMarkPaid(payout.commission_id)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Showing {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
                {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of{" "}
                {pagination.totalCount} payouts
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={!pagination.has_prev}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-400">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={!pagination.has_next}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPayouts;
